/**
 * FolderTreeNode — recursive tree node for the folder sidebar.
 *
 * Supports click-to-select, expand/collapse, inline rename,
 * right-click context menu, and drag-drop targeting.
 */

import { useState, useRef, useEffect } from 'react'
import { ChevronRight, ChevronDown, Folder, FolderOpen } from 'lucide-react'
import type { FolderNode } from '../../types/fileManager'

interface FolderTreeNodeProps {
  node: FolderNode
  depth: number
  selectedId: number | null
  onSelect: (id: number) => void
  onContextMenu: (e: React.MouseEvent, node: FolderNode) => void
  onRenameSave?: (id: number, newName: string) => void
  renamingId: number | null
  /** DnD handlers */
  onDragOver?: (e: React.DragEvent, node: FolderNode) => void
  onDragLeave?: (e: React.DragEvent) => void
  onDrop?: (e: React.DragEvent, node: FolderNode) => void
}

export default function FolderTreeNode({
  node,
  depth,
  selectedId,
  onSelect,
  onContextMenu,
  onRenameSave,
  renamingId,
  onDragOver,
  onDragLeave,
  onDrop,
}: FolderTreeNodeProps) {
  const [expanded, setExpanded] = useState(depth < 2)
  const [dragOver, setDragOver] = useState(false)
  const [renameValue, setRenameValue] = useState(node.name)
  const inputRef = useRef<HTMLInputElement>(null)

  const hasChildren = node.children && node.children.length > 0
  const isSelected = selectedId === node.id
  const isRenaming = renamingId === node.id

  const folderColor =
    node.folder_type === 'date_bucket' ? 'text-[var(--rq-amber)]' :
    node.folder_type === 'source_bucket' ? 'text-[var(--rq-blue)]' :
    node.folder_type === 'project_root' ? 'text-[var(--rq-text-soft)]' :
    'text-[var(--rq-magenta)]'

  const handleClick = () => {
    onSelect(node.id)
    if (hasChildren && !expanded) setExpanded(true)
  }

  const handleChevronClick = (e: React.MouseEvent) => {
    e.stopPropagation()
    setExpanded(!expanded)
  }

  const handleRenameSubmit = () => {
    if (renameValue.trim() && renameValue.trim() !== node.name && onRenameSave) {
      onRenameSave(node.id, renameValue.trim())
    }
  }

  const handleRenameKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') handleRenameSubmit()
    if (e.key === 'Escape' && onRenameSave) onRenameSave(node.id, node.name) // cancel
  }

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setDragOver(true)
    onDragOver?.(e, node)
  }

  const handleDragLeave = (e: React.DragEvent) => {
    setDragOver(false)
    onDragLeave?.(e)
  }

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setDragOver(false)
    onDrop?.(e, node)
  }

  // Focus rename input when entering rename mode
  useEffect(() => {
    if (isRenaming) {
      inputRef.current?.focus()
    }
  }, [isRenaming])

  return (
    <div>
      <div
        className={`fm-tree-node ${isSelected ? 'fm-tree-node-selected' : ''} ${dragOver ? 'fm-tree-node-drop-target' : ''}`}
        style={{ paddingLeft: `${depth * 16 + 8}px` }}
        onClick={handleClick}
        onContextMenu={(e) => { e.preventDefault(); onContextMenu(e, node) }}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        {/* Chevron */}
        <button
          onClick={handleChevronClick}
          className="w-4 h-4 flex items-center justify-center flex-shrink-0"
        >
          {hasChildren ? (
            expanded ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />
          ) : (
            <span className="w-3.5" />
          )}
        </button>

        {/* Folder icon */}
        {isSelected || expanded ? (
          <FolderOpen className={`w-4 h-4 flex-shrink-0 ${folderColor}`} />
        ) : (
          <Folder className={`w-4 h-4 flex-shrink-0 ${folderColor}`} />
        )}

        {/* Name or rename input */}
        {isRenaming ? (
          <input
            ref={inputRef}
            value={renameValue}
            onChange={e => setRenameValue(e.target.value)}
            onBlur={handleRenameSubmit}
            onKeyDown={handleRenameKeyDown}
            className="flex-1 min-w-0 bg-black/40 border border-white/20 rounded px-1 py-0.5 text-xs text-[var(--rq-text)] outline-none focus:border-[var(--rq-blue)]"
            onClick={e => e.stopPropagation()}
          />
        ) : (
          <span className="flex-1 min-w-0 truncate text-xs">{node.name}</span>
        )}

        {/* Asset count badge */}
        {node.asset_count > 0 && !isRenaming && (
          <span className="text-[10px] text-[var(--rq-text-soft)] bg-white/8 px-1.5 py-0.5 rounded-full flex-shrink-0">
            {node.asset_count}
          </span>
        )}
      </div>

      {/* Children */}
      {expanded && hasChildren && (
        <div>
          {node.children.map(child => (
            <FolderTreeNode
              key={child.id}
              node={child}
              depth={depth + 1}
              selectedId={selectedId}
              onSelect={onSelect}
              onContextMenu={onContextMenu}
              onRenameSave={onRenameSave}
              renamingId={renamingId}
              onDragOver={onDragOver}
              onDragLeave={onDragLeave}
              onDrop={onDrop}
            />
          ))}
        </div>
      )}
    </div>
  )
}
